import React from "react";
import { FieldArray, getIn, useFormikContext } from "formik";
import clsx from "clsx";
import {
  Alert,
  Box,
  Button,
  ButtonColors,
  Checkbox,
  Datepicker,
  RadioButtonGroup,
  Select,
  TextField,
  TalentechColors,
  Typography,
} from "@talentech/components";
import DuoEditor from "@/components/DuoEditor/DuoEditor";
import { AD_TEXT_EDITOR_CONFIG } from "@/components/DuoEditor/duoEditorConfig";
import { ApplyType } from "@/redux/reducer/directJobs.types";
import BannerPicker from "./BannerPicker";
import {
  EMPTY_CONTACT_PERSON,
  JobFormValues,
  toLocalDateString,
} from "./jobForm";
import { JobAdTemplateOption } from "./jobAdTemplateOptions";
import {
  APPLY_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  JOB_TYPE_OPTIONS,
  MAX_CONTACT_PERSONS,
  SALARY_PERIOD_OPTIONS,
  WORKPLACE_TYPE_OPTIONS,
} from "./jobFormOptions";

const useJobField = (name: string) => {
  const { values, errors, setFieldValue, setFieldError } =
    useFormikContext<JobFormValues>();
  const value: unknown = getIn(values, name);
  const error: unknown = getIn(errors, name);
  return {
    text: typeof value === "string" ? value : "",
    error: typeof error === "string" ? error : undefined,
    setValue: (value: unknown) => {
      setFieldValue(name, value, false);
      setFieldError(name, undefined);
    },
  };
};

const fieldId = (name: string) => `job-${name.replace(/\./g, "-")}`;

const RequiredMark = () => <span className="text-danger"> *</span>;

const FieldLabel: React.FC<{
  name: string;
  label: string;
  required?: boolean;
}> = ({ name, label, required }) => (
  <label className="form-label" htmlFor={fieldId(name)}>
    {label}
    {required && <RequiredMark />}
  </label>
);

const FieldFeedback: React.FC<{ error?: string; hint?: string }> = ({
  error,
  hint,
}) => (
  <>
    {error ? (
      <small className="text-danger d-block mt-1">{error}</small>
    ) : (
      hint && <small className="text-muted d-block mt-1">{hint}</small>
    )}
  </>
);

const TextInput: React.FC<{
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  type?: "text" | "email" | "url" | "tel";
  inputMode?: "decimal";
  className?: string;
}> = ({
  name,
  label,
  required,
  placeholder,
  type = "text",
  inputMode,
  className,
}) => {
  const { text, error, setValue } = useJobField(name);
  return (
    <div className={clsx("mb-3", className)}>
      <FieldLabel name={name} label={label} required={required} />
      <TextField
        id={fieldId(name)}
        name={name}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={text}
        invalid={!!error}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setValue(event.target.value)
        }
      />
      <FieldFeedback error={error} />
    </div>
  );
};

const SelectInput: React.FC<{
  name: string;
  label: string;
  options: { value: string; label: string }[];
  required?: boolean;
  className?: string;
}> = ({ name, label, options, required, className }) => {
  const { text, error, setValue } = useJobField(name);
  return (
    <div className={clsx("mb-3", className)}>
      <FieldLabel name={name} label={label} required={required} />
      <Select
        inputId={fieldId(name)}
        ariaLabel={label}
        options={options}
        value={options.find((option) => option.value === text) ?? null}
        validationError={!!error}
        onChange={(option: unknown) => {
          if (
            typeof option === "object" &&
            option !== null &&
            "value" in option
          ) {
            setValue(option.value);
          }
        }}
      />
      <FieldFeedback error={error} />
    </div>
  );
};

const DateInput: React.FC<{
  name: string;
  label: string;
  hint?: string;
  hideLabel?: boolean;
  className?: string;
}> = ({ name, label, hint, hideLabel, className }) => {
  const { text, error, setValue } = useJobField(name);
  return (
    <div className={clsx("mb-3", className)}>
      <label className="d-block mb-0">
        <span
          className={clsx("form-label d-block", {
            "visually-hidden": hideLabel,
          })}
        >
          {label}
        </span>
        <Datepicker
          value={text ? [text] : []}
          validationError={!!error}
          options={{
            format: "Y-m-d",
            placeholder: "YYYY-MM-DD",
            isClearable: Boolean(text),
            onChange: (dates: Date[]) =>
              setValue(dates[0] ? toLocalDateString(dates[0]) : ""),
          }}
        />
      </label>
      <FieldFeedback error={error} hint={hint} />
    </div>
  );
};

const Section: React.FC<{
  title: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ title, required, hint, error, children }) => (
  <Box paper rounded spacing={4} className="mb-4">
    <Typography variant="h2">
      {title}
      {required && <RequiredMark />}
    </Typography>
    {hint && (
      <Typography variant="p" className="text-muted">
        {hint}
      </Typography>
    )}
    {error && <small className="text-danger d-block mb-3">{error}</small>}
    {children}
  </Box>
);

const JobSection = () => {
  const { values } = useFormikContext<JobFormValues>();
  const { setValue: setHasNoDeadline } = useJobField("has_no_deadline");
  const { error: salaryError } = useJobField("salary");
  return (
    <Section title="The job" hint="Shown in the ad header and on job lists">
      <TextInput name="title" label="Job title" required />
      <div className="row">
        <TextInput name="department" label="Department" className="col-md-6" />
        <TextInput name="category" label="Category" className="col-md-6" />
      </div>
      <div className="row">
        <TextInput
          name="location"
          label="Location"
          required
          className="col-lg-6"
        />
        <SelectInput
          name="workplace_type"
          label="Workplace type"
          options={WORKPLACE_TYPE_OPTIONS}
          className="col-md-6 col-lg-3"
        />
        <SelectInput
          name="job_type"
          label="Job type"
          options={JOB_TYPE_OPTIONS}
          className="col-md-6 col-lg-3"
        />
      </div>
      <fieldset className="mb-3 col-md-6 col-lg-4">
        <legend className="form-label">Application deadline</legend>
        <Checkbox
          id="job-has-no-deadline"
          label="No set deadline"
          checked={values.has_no_deadline}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setHasNoDeadline(event.target.checked)
          }
        />
        {!values.has_no_deadline && (
          <DateInput
            name="application_deadline"
            label="Application deadline"
            hideLabel
            className="mt-2"
          />
        )}
      </fieldset>
      <fieldset className="mb-3">
        <legend className="form-label">Salary</legend>
        <div className="row">
          <SelectInput
            name="salary.currency"
            label="Currency"
            options={CURRENCY_OPTIONS}
            className="col-md-3"
          />
          <TextInput
            name="salary.from"
            label="From"
            inputMode="decimal"
            className="col-md-3"
          />
          <TextInput
            name="salary.to"
            label="To"
            inputMode="decimal"
            className="col-md-3"
          />
          <SelectInput
            name="salary.period"
            label="Period"
            options={SALARY_PERIOD_OPTIONS}
            className="col-md-3"
          />
        </div>
        <FieldFeedback error={salaryError} />
      </fieldset>
    </Section>
  );
};

const AdTextSection = () => {
  const { text, error, setValue } = useJobField("ad_text");
  return (
    <Section title="Ad text" required>
      <Box
        className={clsx("form-control p-0", {
          "border border-opacity-100 border-danger": !!error,
        })}
        paper
        rounded
      >
        <DuoEditor
          value={text}
          onChange={setValue}
          config={AD_TEXT_EDITOR_CONFIG}
        />
      </Box>
      <FieldFeedback error={error} />
    </Section>
  );
};

const BannerSection = () => {
  const { text, error, setValue } = useJobField("banner_url");
  return (
    <Section title="Banner image" hint="Shown at the top of the job ad">
      <BannerPicker value={text} onChange={setValue} />
      <FieldFeedback
        error={error}
        hint="Uploads are saved in the Media Library."
      />
    </Section>
  );
};

const ContactPersonsSection = () => {
  const { values } = useFormikContext<JobFormValues>();
  return (
    <Section
      title="Contact persons"
      hint="Shown on the job ad so candidates know whom to ask"
    >
      <FieldArray name="contact_persons">
        {({ push, remove }) => (
          <>
            {values.contact_persons.map((_person, index) => (
              <Box key={index} className="border rounded p-3 mb-3">
                <div className="row">
                  <TextInput
                    name={`contact_persons.${index}.name`}
                    label="Name"
                    required
                    className="col-md-6"
                  />
                  <TextInput
                    name={`contact_persons.${index}.title`}
                    label="Job title"
                    className="col-md-6"
                  />
                  <TextInput
                    name={`contact_persons.${index}.email`}
                    label="Email"
                    type="email"
                    className="col-md-6"
                  />
                  <TextInput
                    name={`contact_persons.${index}.phone`}
                    label="Phone"
                    type="tel"
                    className="col-md-6"
                  />
                </div>
                <Button
                  label="Remove"
                  outline
                  color={ButtonColors.DANGER}
                  icon={<i className="fa-sharp fa-light fa-trash me-2" />}
                  onClick={() => remove(index)}
                />
              </Box>
            ))}
            <Button
              label="Add contact person"
              outline
              color={ButtonColors.PRIMARY}
              icon={<i className="fa-sharp fa-light fa-plus me-2" />}
              disabled={values.contact_persons.length >= MAX_CONTACT_PERSONS}
              onClick={() => push(EMPTY_CONTACT_PERSON)}
            />
          </>
        )}
      </FieldArray>
    </Section>
  );
};

const ApplySection = () => {
  const { values } = useFormikContext<JobFormValues>();
  const { setValue: setApplyType } = useJobField("apply.type");
  const { error } = useJobField("apply");
  return (
    <Section
      title="How candidates apply"
      hint="What the Apply button on the job ad does"
      error={error}
    >
      <RadioButtonGroup
        name="apply-type"
        options={APPLY_TYPE_OPTIONS}
        value={values.apply.type}
        onChange={setApplyType}
      />
      <div className="mt-3">
        {values.apply.type === ApplyType.URL ? (
          <TextInput
            name="apply.url"
            label="Application page URL"
            type="url"
            placeholder="https://..."
            required
          />
        ) : (
          <>
            <TextInput
              name="apply.email"
              label="Email address for applications"
              type="email"
              required
            />
            <Alert
              simple
              color={TalentechColors.WARNING}
              className="py-2 px-3 mb-0"
              message={
                <span>
                  Applications arrive as emails, so candidate data is kept in
                  this inbox. Handle it as your privacy policy says.
                </span>
              }
            />
          </>
        )}
      </div>
    </Section>
  );
};

const PublishingSection: React.FC<{
  templateOptions: JobAdTemplateOption[];
}> = ({ templateOptions }) => (
  <Section title="Publishing">
    <SelectInput
      name="job_ad_template_id"
      label="Job ad template"
      required
      options={templateOptions.map(({ value, label, careerSites }) => ({
        value,
        label: `${label} — ${careerSites.join(", ")}`,
      }))}
    />
    <div className="row">
      <DateInput
        name="show_from"
        label="Show from"
        hint="Leave empty to publish now"
        className="col-md-6 col-lg-4"
      />
      <DateInput
        name="show_until"
        label="Show until"
        hint="Leave empty to show it until you unpublish it"
        className="col-md-6 col-lg-4"
      />
    </div>
  </Section>
);

const JobFormFields: React.FC<{ templateOptions: JobAdTemplateOption[] }> = ({
  templateOptions,
}) => (
  <>
    <JobSection />
    <AdTextSection />
    <BannerSection />
    <ContactPersonsSection />
    <ApplySection />
    <PublishingSection templateOptions={templateOptions} />
  </>
);

export default JobFormFields;
